import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import {
	combineAll,
	DcBaseComponent,
	toImmutableMap,
} from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	CellHoverChangedEvent,
	ContentReadyEvent,
	SelectionChangedEvent,
} from 'devextreme/ui/data_grid';
import { Map, Set } from 'immutable';
import {
	firstValueFrom,
	map,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import {
	checkStringEquality,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from '../../../core/dx-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { SelectedMember } from '../../../ui/components/app-member-list/member-item.entity';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	ExpositionConsumerMappingEntity,
	TargetConsumer,
} from '../../domain/exposition-consumer-mapping.entity';
import { ConsumerMappingPopupComponent } from './consumer-mapping-popup/consumer-mapping-popup.component';
import { ExpositionConsumerMappingListEntity } from './exposition-consumers-mapping-list.entity';
import { ExpositionConsumersMappingListProvider } from './exposition-consumers-mapping-list.provider';

@Component({
	selector: 'app-exposition-consumers-mapping',
	templateUrl: 'exposition-consumers-mapping.component.html',
	inputs: ['consumersToMap'],
	styleUrls: ['exposition-consumers-mapping.component.scss'],
	providers: [ExpositionConsumersMappingListProvider],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpositionConsumersMappingComponent
	extends DcBaseComponent
	implements OnInit
{
	protected readonly DcLoaders = DcLoaders;
	protected readonly DcIcons = DcIcons;
	protected readonly ActionIcons = ActionIcons;
	protected readonly DxOperations = DxOperations;

	@Output()
	public assignTargetConsumer = new EventEmitter<
		Array<ExpositionConsumerMappingEntity>
	>();

	@Output()
	public activateAutoMapping = new EventEmitter<string | undefined>();

	@Output()
	public reloadUsers = new EventEmitter<void>();

	public memberTypesHeaderFilter = [
		{
			text: $localize`:i18n=@@general.memberType.groups:`,
			value: ['isGroup', '=', true],
		},
		{
			text: $localize`:i18n=@@general.memberType.users:`,
			value: ['isGroup', '=', false],
		},
	];
	public targetsHeaderFilter: Array<DxHeaderFilterItem> = [];

	public panelActions: Array<PanelAction> = [];
	public toggleActions = false;
	public selectionCount = 0;
	public currentlyHovered = -1;

	public vo$: Observable<{
		consumersToMapList: ExpositionConsumerMappingListEntity;
	}>;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly consumersToMap$ = new ReplaySubject<
		Map<string, ExpositionConsumerMappingEntity>
	>(1);

	public constructor(
		private readonly consumersMappingListProvider: ExpositionConsumersMappingListProvider,
		private readonly modalService: AppModalService,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'app-consumers-mapping';

		const consumersToMapList$ = this.toObservable<
			Array<ExpositionConsumerMappingEntity>
		>('consumersToMap').pipe(
			map((consumers) => toImmutableMap(consumers, 'id')),
			tap((items) => this.consumersToMap$.next(items)),
			map((items) => this.consumersMappingListProvider.setListElements(items)),
			tap((l) => {
				this.targetsHeaderFilter = l
					.extractUniqTargets(NoopValue)
					.map(HeaderFilterDefaultMapper)
					.toArray();
				const selected = this.grid?.instance.getSelectedRowKeys();
				if (selected && selected.length > 0) {
					this.grid?.instance.clearSelection();
				}
			})
		);

		this.vo$ = combineAll({
			consumersToMapList: consumersToMapList$,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.handleSubscription();
	}

	/*
	public triggerReloadUsers(): void {
		this.reloadUsers.emit();
	}
	*/

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onGridContentReady(event: ContentReadyEvent): void {
		event.component.updateDimensions();
	}

	public onSelectionChange(event: SelectionChangedEvent): void {
		this.toggleActions = event.selectedRowsData.length > 0;
		this.selectionCount = event.selectedRowsData.length;
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public async uniMapping(
		consumerToMap: ExpositionConsumerMappingEntity
	): Promise<void> {
		const selectedMembers: Map<number, SelectedMember> = Map(
			consumerToMap.targetConsumers
				.toList()
				.toArray()
				.map<[number, SelectedMember]>((consumer: TargetConsumer) => [
					consumer.id,
					{
						id: consumer.id,
						name: consumer.name,
						isGroup: consumer.isGroup,
						avatar: consumer.avatar,
					},
				])
		);

		const modalRef = this.modalService.openPopup<{
			selectedConsumers: Map<number, SelectedMember>;
		}>(
			ConsumerMappingPopupComponent,
			{
				title: $localize`:i18n=@@expositions.consumers.mapping.action.header:`,
				subTitle: '',
				width: '85%',
				height: '90%',
				selectedConsumers: selectedMembers,
			},
			{
				disableClose: true,
			}
		);
		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const { selectedConsumers } = modalResult;

		const targetConsumers: Set<TargetConsumer> = Set(
			selectedConsumers
				.toList()
				.toArray()
				.map((member: SelectedMember) => ({
					id: member.id,
					name: member.name,
					isGroup: member.isGroup,
					avatar: member.avatar,
				}))
		);

		const updated = consumerToMap.updateTargetConsumers(targetConsumers);

		this.assignTargetConsumer.emit([updated]);
	}

	public removeSelectedTargetConsumer(
		consumerToMap: ExpositionConsumerMappingEntity,
		removedConsumer: TargetConsumer
	): void {
		const { targetConsumers } = consumerToMap;

		const updatedTargetConsumers = targetConsumers.delete(removedConsumer);

		const updated = consumerToMap.updateTargetConsumers(updatedTargetConsumers);

		this.assignTargetConsumer.emit([updated]);
	}

	public resetMapping(consumerToMap: ExpositionConsumerMappingEntity): void {
		const { targetConsumers } = consumerToMap;

		if (targetConsumers.size === 0) {
			return;
		}

		const updated = consumerToMap.updateTargetConsumers(Set());

		this.assignTargetConsumer.emit([updated]);
	}

	public async resetSelectedMapping(): Promise<void> {
		if (!this.grid) {
			return;
		}

		const consumersToMap = await firstValueFrom(this.consumersToMap$);
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;

		const updatedConsumers = consumersToMap
			.filter((consumer) => selectedRowKeys.includes(consumer.id))
			.map((consumer) => consumer.updateTargetConsumers(Set()));

		this.assignTargetConsumer.emit(updatedConsumers.toList().toArray());
	}

	public autoMapping(id?: string): void {
		this.activateAutoMapping.emit(id);
	}

	public calculateTargetsFilterExpression(
		value: string,
		filterOperation: DxOperations,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: ExpositionConsumerMappingEntity): boolean => {
			const targets = column.calculateCellValue(data) as Set<TargetConsumer>;
			if (targets.size === 0 && value === NoopValue) {
				return true;
			}

			return (
				!!targets &&
				!!targets.find((v) =>
					checkStringEquality(v.name, value, filterOperation)
				)
			);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private async multiMapping(): Promise<void> {
		if (!this.grid) {
			return;
		}

		const consumerToMap = await firstValueFrom(this.consumersToMap$);
		const selectedRowKeys = this.grid.selectedRowKeys as Array<string>;

		const selectedMembers = selectedRowKeys.reduce((acc, curr) => {
			const consumer = consumerToMap.get(curr);

			if (!consumer) {
				return acc;
			}

			consumer.targetConsumers
				.toList()
				.toArray()
				.forEach((targetConsumer: TargetConsumer) => {
					const { id, name, isGroup, avatar } = targetConsumer;
					if (!acc.has(id)) {
						acc = acc.set(id, {
							id,
							name,
							isGroup,
							avatar,
						});
					}
				});

			return acc;
		}, Map<number, SelectedMember>());

		const modalRef = this.modalService.openPopup<{
			selectedConsumers: Map<number, SelectedMember>;
		}>(
			ConsumerMappingPopupComponent,
			{
				title: $localize`:i18n=@@expositions.consumers.mapping.action.header:`,
				subTitle: '',
				width: '85%',
				height: '90%',
				selectedConsumers: selectedMembers,
			},
			{
				disableClose: true,
			}
		);

		const modalResult = await firstValueFrom(modalRef.afterClosed());

		if (!modalResult) {
			return;
		}

		const { selectedConsumers } = modalResult;

		const targetConsumers: Set<TargetConsumer> = Set(
			selectedConsumers
				.toList()
				.toArray()
				.map((member: SelectedMember) => ({
					id: member.id,
					name: member.name,
					isGroup: member.isGroup,
					avatar: member.avatar,
				}))
		);

		const selectedConsumersToMap = consumerToMap.filter((value, key) =>
			selectedRowKeys.includes(key)
		);

		const updatedConsumers = selectedConsumersToMap.reduce<
			Array<ExpositionConsumerMappingEntity>
		>((acc, unmapped) => {
			acc.push(unmapped.updateTargetConsumers(targetConsumers));

			return acc;
		}, []);

		this.assignTargetConsumer.emit(updatedConsumers);

		this.grid.instance.clearSelection();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [
			ActionIcons.AutoMap,
			ActionIcons.Mapper,
			ActionIcons.ResetSelection,
		];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);

		this.panelActions = [
			{
				order: 0,
				command: (): void => this.autoMapping(),
				iconName: actionMap.get(ActionIcons.AutoMap)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@expositions.consumers.mapping.autoMapping:`,
			},
			{
				order: 1,
				command: (): Promise<void> => this.multiMapping(),
				iconName: actionMap.get(ActionIcons.Mapper)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@expositions.consumers.mapping.multiMapping:`,
				typed: 'plain-context-action',
			},
			{
				order: 2,
				command: (): Promise<void> => this.resetSelectedMapping(),
				iconName: actionMap.get(ActionIcons.ResetSelection)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@expositions.consumers.mapping.reset:`,
				typed: 'plain-context-action',
			},
		];
	}

	private handleSubscription(): void {
		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}
}
